import { useState } from "react";
import { Search, Phone, Plus, Eye, MessageCircle } from "lucide-react";
import ClientList from "../../../../components/features/support/clients/ClientsList";
import ClientDetailsModal from "../../../../components/features/support/clients/ClientDetailsModal";
import MenuModal, { Menu } from "../../../../components/ui/MenuModal";
import useClients from "../../../../hooks/support/clients/useClients";
import useTickets from "../../../../hooks/support/tickets/useTickets";
import useCalls from "../../../../hooks/support/calls/useCalls";
import { alertSuccess } from "../../../../helpers/alertError";
import { Client } from "../../../../types/features/support/Client";
import PageListLayout from "../../../../layouts/PageListLayout";
import { useNavigate } from "react-router-dom";

export default function ClientsPage() {
  const { clients, searchClients, searchResults, searchClientByPhone, clearSearch, loading } = useClients();
  const navigate = useNavigate();
  const { tickets } = useTickets();
  const { calls } = useCalls();
  const [searchQuery, setSearchQuery] = useState("");

  // États de sélection (Comme sur les tickets, on sépare le menu volant du modal de détails)
  const [selectedClient, setSelectedClient] = useState<Client | null>(null); // Pour le MenuModal
  const [clientToView, setClientToView] = useState<Client | null>(null); // Pour le ClientDetailsModal
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  const displayClients = searchQuery.trim() ? searchResults : clients;

  const handleSearch = async () => {
    if (searchQuery.trim()) {
      await searchClients(searchQuery);
    } else {
      clearSearch();
    }
  };

  const handlePhoneSearch = async () => {
    if (searchQuery.trim()) {
      const client = await searchClientByPhone(searchQuery);
      if (client) {
        setClientToView(client);
        setDetailsModalOpen(true);
      }
      setSearchQuery("");
      clearSearch();
    }
  };

  const getClientCalls = (clientId: string) => {
    return calls.filter((call) => call.client_id === clientId).slice(0, 5);
  };

  const getClientTickets = (clientId: string) => {
    return tickets
      .filter((ticket) => ticket.client_id === clientId && ticket.status !== "resolu" && ticket.status !== "ferme")
      .slice(0, 5);
  };

  // Configuration du menu d'actions (Appeler, Créer ticket, Voir détails)
  const getActionsMenu = (client: Client): Menu[] => {
    return [
      {
        label: "Discuter ",
        icon: MessageCircle,
        onClick: () => {
          navigate("/support/chat", { state: { userId: client.user_id } });
        },
      },
      {
        label: "Créer ticket",
        icon: Plus,
        onClick: () => {
          alertSuccess(`Créer un ticket pour le client`);
          setSelectedClient(null); // Ferme le menu volant
        },
      },
      {
        label: "Voir détails",
        icon: Eye,
        onClick: () => {
          navigate("/support/clients/details", { state: client });
        },
      },
    ];
  };

  // Sécurité : masquer le menu volant si le gros modal de détails est ouvert par-dessus
  const isAnySubModalOpen = detailsModalOpen;

  return (
    <PageListLayout title="Gestion des clients">
      <div className="px-6 py-4 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
              <p className="text-sm text-gray-500 mt-1">Recherchez et consultez les informations clients</p>
            </div>
          </div>

          {/* Recherche */}
          <div className="mt-6">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher par nom, email ou téléphone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/50"
                />
              </div>
              <button
                onClick={handleSearch}
                className="px-4 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90"
              >
                Rechercher
              </button>
              <button
                onClick={handlePhoneSearch}
                className="px-4 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700"
              >
                Rechercher par téléphone
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-8 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <ClientList
            clients={displayClients}
            onAction={(client) => {
              setSelectedClient(client); // Ouvre le MenuModal au clic sur la ligne
            }}
            selectable={false}
          />
        )}
      </div>

      {/* Menu modal volant pour les actions du client */}
      {selectedClient && !isAnySubModalOpen && (
        <MenuModal
          title={`${selectedClient.full_name || "Actions client"}`}
          isOpen={true}
          onClose={() => setSelectedClient(null)}
          icon={null}
          menu={getActionsMenu(selectedClient)}
        />
      )}

      {/* Modal détails client */}
      <ClientDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setClientToView(null);
        }}
        client={clientToView}
        recentCalls={clientToView ? getClientCalls(clientToView.id) : []}
        recentTickets={clientToView ? getClientTickets(clientToView.id) : []}
        onNewTicket={(clientId) => {
          setDetailsModalOpen(false);
          alertSuccess(`Créer un ticket pour le client`);
        }}
        onCall={(phone) => {
          alertSuccess(`Appel vers ${phone}`);
        }}
      />
    </PageListLayout>
  );
}
